import { inspect } from 'util'
import React from 'react'
// eslint-disable-next-line no-restricted-imports
import Bugsnag, { BreadcrumbType, OnErrorCallback } from '@bugsnag/react-native'
import BugsnagPerformance, { ReactNativeConfiguration } from '@bugsnag/react-native-performance'
import { BugsnagPluginReactResult } from '@bugsnag/plugin-react'
import BugsnagPluginReactNavigation, {
  BugsnagPluginReactNavigationResult,
} from '@bugsnag/plugin-react-navigation'
import { NavigationContainer } from '@react-navigation/native'
import { Platform } from 'react-native'
import Config from 'react-native-config'
import { UserPrimaryInfo as User } from '@src/models/user.types'
import App from '@config/app'
import HotUpdater from '@src/config/hotUpdater'

class BugsnagClient {
  configured: boolean
  user?: User
  admin?: User

  constructor() {
    this.configured = false
  }

  configure() {
    if (this.configured) {
      return
    }

    if (Config.ENV !== 'development') {
      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        const apiKey = Platform.OS === 'ios' ? Config.IOS_BUGSNAG_KEY : Config.ANDROID_BUGSNAG_KEY
        const codeBundleId = HotUpdater.useCodeBundleId
          ? `${App.version}-${HotUpdater.revisionSuffix}`
          : undefined

        this.configured = true
        Bugsnag.start({
          codeBundleId,
          plugins: [new BugsnagPluginReactNavigation()],
          onError: (event) => {
            const error = event.errors[0]
            if (
              error.errorClass === 'AxiosError' &&
              error.errorMessage === 'Network Error' &&
              error.stacktrace[0]?.file?.includes('stream-chat')
            ) {
              return false
            }

            const isPeripheralDisconnectMessage =
              typeof error.errorMessage === 'string' &&
              (error.errorMessage.startsWith('Peripheral did disconnect:') ||
                error.errorMessage.includes('Bluetooth - Peripheral did disconnect:'))

            if (isPeripheralDisconnectMessage) {
              return false
            }

            if (error.errorClass === 'AbortError') {
              return false
            }

            if (this.user) {
              // Send only the id — email/name are PII we don't share with Bugsnag.
              event.setUser(this.user.id)
            }
            if (this.admin) {
              event.addMetadata('admin', { id: this.admin.id })
            }
          },
        })

        if (apiKey) {
          const options: ReactNativeConfiguration = {
            apiKey,
            bugsnag: Bugsnag,
          }

          if (codeBundleId) {
            options.codeBundleId = codeBundleId
          }

          BugsnagPerformance.start(options)
        }
      }
    }
  }

  setUser(user?: User, admin?: User) {
    this.user = user
    this.admin = admin
    if (!this.configured) {
      this.configure()
    }
    if (!this.configured) {
      return
    }

    if (user) {
      // Send only the id — email/name are PII we don't share with Bugsnag.
      Bugsnag.setUser(user.id)
    } else {
      Bugsnag.setUser()
    }

    if (admin) {
      Bugsnag.addMetadata('admin', { id: admin.id })
    } else {
      Bugsnag.clearMetadata('admin')
    }
  }

  reset() {
    if (!this.configured) {
      return
    }

    Bugsnag.setUser()
    Bugsnag.clearMetadata('admin')
  }

  leaveBreadcrumb(message: string, metadata = {}, type: BreadcrumbType = 'request') {
    if (!this.configured) {
      return
    }

    Bugsnag.leaveBreadcrumb(message, metadata, type)
  }

  notify(error: Error | string, onError?: OnErrorCallback) {
    if (!this.configured) {
      return
    }

    if (error && error instanceof Error) {
      Bugsnag.notify(error, onError)
    } else {
      Bugsnag.notify(new Error(inspect(error)), onError)
    }
  }

  createErrorBoundary() {
    if (!this.configured) {
      this.configure()
    }

    if (!this.configured) {
      return undefined
    }

    const reactPlugin = Bugsnag.getPlugin('react') as BugsnagPluginReactResult | undefined
    return reactPlugin?.createErrorBoundary(React)
  }

  createNavigationContainer(navigationContainer = NavigationContainer) {
    if (!this.configured) {
      this.configure()
    }
    if (!this.configured) {
      return navigationContainer
    }

    const reactNavigationPlugin = Bugsnag.getPlugin('reactNavigation') as
      | BugsnagPluginReactNavigationResult
      | undefined

    if (!reactNavigationPlugin?.createNavigationContainer) {
      return navigationContainer
    }

    // The returned BugsnagNavigationContainer has exactly the same usage
    // except now it tracks route information to send with your error reports
    const BugsnagNavigationContainer = reactNavigationPlugin.createNavigationContainer(
      navigationContainer,
    )
    return BugsnagNavigationContainer
  }
}

export default new BugsnagClient()
